// The render studio's calls to the render API (W15, apps/api/render/README.md): one scene per
// site photo, the canvas state as the scene's items, and render jobs followed until done.

import type { Schemas } from "@artisan/api-client";

import { api, unwrap } from "../lib/api";
import { projectTitle } from "../project/actions";
import type { PhotoRef, Project, RenderItemDraft, RenderResultRow } from "../project/model";
import { photoBlob, updateProject } from "../project/store";

export type Asset = Schemas["AssetOut"];
export type Result = Schemas["ResultOut"];
export type Offer = Schemas["NearbyOfferOut"];

export const MAX_ITEMS = 12;

const round3 = (v: number) => Math.round(v * 1000) / 1000;
const clamp01 = (v: number) => Math.min(1, Math.max(0, v));

export function photoAspect(photo: Pick<PhotoRef, "width_px" | "height_px"> | undefined): number {
  return (photo?.width_px ?? 4) / (photo?.height_px ?? 3);
}

/** The item's outline on the photo, as shares of the photo's width and height. Objects take
 * the product's real proportions (as the server does); surfaces are a free rectangle. */
export function itemBox(it: RenderItemDraft, aspect: number): { w: number; h: number } {
  if (it.mode === "surface") return { w: it.width, h: it.height ?? Math.min(0.9, it.width * aspect * 0.5) };
  return { w: it.width, h: it.width * aspect * (it.aspect ?? 0.75) };
}

/** A surface's region as four corners, clockwise from top left (the API needs one). */
export function quadOf(it: RenderItemDraft, aspect: number): [number, number][] {
  const { w, h } = itemBox(it, aspect);
  const [x0, x1] = [clamp01(it.center_x - w / 2), clamp01(it.center_x + w / 2)];
  const [y0, y1] = [clamp01(it.center_y - h / 2), clamp01(it.center_y + h / 2)];
  return [
    [round3(x0), round3(y0)],
    [round3(x1), round3(y0)],
    [round3(x1), round3(y1)],
    [round3(x0), round3(y1)],
  ];
}

/** Where a new item goes: the middle of the lower half, stepped so items don't stack. */
export function nextSpot(count: number): { x: number; y: number } {
  const steps = [
    [0.5, 0.62],
    [0.32, 0.6],
    [0.68, 0.6],
    [0.5, 0.42],
    [0.3, 0.4],
    [0.7, 0.4],
  ] as const;
  const s = steps[count % steps.length] ?? steps[0];
  const shift = Math.floor(count / steps.length) * 0.04;
  return { x: s[0] + shift, y: s[1] + shift };
}

export function draftFromAsset(a: Asset, offer: Offer | undefined, count: number): RenderItemDraft {
  const spot = nextSpot(count);
  const surface = a.mode === "surface";
  const aspect = !surface && a.width_mm && a.height_mm ? a.height_mm / a.width_mm : null;
  return {
    asset_id: a.id,
    product_id: a.product_id,
    title: a.title_mn,
    mode: surface ? "surface" : "object",
    center_x: spot.x,
    center_y: spot.y,
    width: surface ? 0.4 : 0.22,
    ...(surface ? { height: 0.22 } : {}),
    aspect,
    rotation_deg: 0,
    note: "",
    thumb_url: a.thumb_url ?? null,
    price_mnt: offer?.price_mnt ?? null,
    supplier: offer ? `${offer.branch_name} · ${(offer.distance_m / 1000).toFixed(1)} км` : null,
  };
}

/** The lowest nearby offer for each product (one call for the whole list). */
export async function bestOffers(productIds: string[], { lat, lng }: { lat?: number | undefined; lng?: number | undefined }): Promise<Map<string, Offer>> {
  const best = new Map<string, Offer>();
  const ids = [...new Set(productIds)];
  if (lat === undefined || lng === undefined || !ids.length) return best;
  const r = await unwrap(api().POST("/v1/catalog/offers/nearby", { body: { product_ids: ids, lat, lng, radius_km: 30, delivers_only: false } }));
  for (const o of r.items) {
    const cur = best.get(o.product_id);
    if (!cur || o.price_mnt < cur.price_mnt) best.set(o.product_id, o);
  }
  return best;
}

/** Create the render scene for the base photo (upload + confirm), once per photo. */
export async function ensureScene(project: Project, photo: PhotoRef): Promise<string> {
  if (project.render.scene_id && project.render.scene_photo_id === photo.id) return project.render.scene_id;
  const blob = await photoBlob(photo);
  if (!blob) throw new Error("Зураг төхөөрөмжөөс олдсонгүй");
  const scene = await unwrap(
    api().POST("/v1/render/scenes", {
      body: {
        base: { kind: "photo", content_type: photo.content_type === "image/png" ? "image/png" : "image/jpeg", byte_size: photo.size_bytes, sha256: photo.sha256 },
        title: projectTitle(project).slice(0, 200),
        deal_id: project.deal_id,
      },
    }),
  );
  if (scene.upload) {
    const put = await fetch(scene.upload.url, { method: scene.upload.method, headers: scene.upload.headers, body: blob });
    if (!put.ok) throw new Error("Зургийг хадгалах сан руу илгээж чадсангүй");
  }
  await unwrap(api().POST("/v1/render/scenes/{scene_id}/base/confirm", { params: { path: { scene_id: scene.id } } }));
  await updateProject(
    project.id,
    (p) => {
      p.render.scene_id = scene.id;
      p.render.scene_photo_id = photo.id;
    },
    { edit: false },
  );
  return scene.id;
}

/** Send the canvas (the whole list) and start a render of it. */
export async function startRender(
  project: Project,
  photo: PhotoRef,
  quality: "preview" | "final",
  note: string,
  onStep: (text: string) => void,
  choice: { engine?: string | null; resolution?: string | null } = {},
): Promise<Result> {
  onStep("Зургийг сервер рүү илгээж байна…");
  const sceneId = await ensureScene(project, photo);
  onStep("Байрлалыг хадгалж байна…");
  const aspect = photoAspect(photo);
  await unwrap(
    api().PUT("/v1/render/scenes/{scene_id}/items", {
      params: { path: { scene_id: sceneId } },
      body: {
        items: project.render.items.map((it) => ({
          asset_id: it.asset_id,
          placement: {
            center_x: round3(clamp01(it.center_x)),
            center_y: round3(clamp01(it.center_y)),
            width: round3(Math.min(1, Math.max(0.01, it.width))),
            rotation_deg: Math.round(it.rotation_deg),
            ...(it.mode === "surface" ? { quad: quadOf(it, aspect) } : {}),
          },
          note: (it.note ?? "").trim().slice(0, 500),
        })),
      },
    }),
  );
  onStep("Render эхлүүлж байна…");
  const r = await unwrap(api().POST("/v1/render/scenes/{scene_id}/renders", { params: { path: { scene_id: sceneId } }, body: { quality, ...(note.trim() ? { note: note.trim().slice(0, 500) } : {}), ...renderChoice(choice) } }));
  const items = project.render.items.length;
  await updateProject(
    project.id,
    (p) => {
      p.render.results = [rowOf(r, { items }), ...p.render.results.filter((x) => x.id !== r.id)].slice(0, 12);
    },
    { edit: false },
  );
  return r;
}

/** The engine and resolution the rep picked; left out (the server's default) when none was. */
export function renderChoice(c: { engine?: string | null; resolution?: string | null }): { engine?: string; resolution?: "1K" | "2K" | "4K" } {
  const res = c.resolution === "1K" || c.resolution === "2K" || c.resolution === "4K" ? c.resolution : undefined;
  return { ...(c.engine ? { engine: c.engine } : {}), ...(res ? { resolution: res } : {}) };
}

export function isRunning(row: Pick<RenderResultRow, "status">): boolean {
  return row.status === "queued" || row.status === "running";
}

export function rowOf(r: Result, extra: Partial<RenderResultRow> = {}): RenderResultRow {
  const seconds = r.finished_at ? Math.max(1, Math.round((Date.parse(r.finished_at) - Date.parse(r.created_at)) / 1000)) : null;
  return {
    id: r.id,
    status: r.status,
    output_url: r.output_url ?? null,
    created_at: r.created_at,
    error_code: r.error_code,
    quality: r.quality,
    progress: r.progress,
    note: r.note ?? null,
    engine: r.engine ?? null,
    resolution: r.resolution ?? null,
    seconds,
    ...extra,
  };
}

/** Bring the stored rows up to date (status, and fresh image links: they expire). */
export async function refreshResults(project: Project, ids?: string[]): Promise<void> {
  const want = ids ?? project.render.results.map((r) => r.id);
  if (!want.length) return;
  const fresh = await Promise.all(
    want.map((id) => unwrap(api().GET("/v1/render/results/{result_id}", { params: { path: { result_id: id } } })).catch(() => null)),
  );
  await updateProject(
    project.id,
    (p) => {
      for (const r of fresh) {
        if (!r) continue;
        const row = p.render.results.find((x) => x.id === r.id);
        if (row) Object.assign(row, rowOf(r, { items: row.items }));
        if (p.render.approved?.result_id === r.id) p.render.approved.output_url = r.output_url ?? null;
      }
    },
    { edit: false },
  );
}

/** Why a render failed, for the rep. */
export function failureText(code: string | null): string {
  switch (code) {
    case "model_refused":
      return "AI загвар энэ зургийг боловсруулахаас татгалзлаа. Зураг эсвэл барааг сольж үзнэ үү.";
    case "no_image":
    case "bad_output":
      return "AI загвар зураг буцаасангүй. Дахин оролдоно уу.";
    case "provider_timeout":
    case "timed_out":
      return "Хугацаа хэтэрлээ. Дахин оролдоно уу.";
    case "provider_rate_limited":
      return "AI үйлчилгээ завгүй байна. Хэдэн секундийн дараа дахин оролдоно уу.";
    case "bad_input":
      return "Зураг эсвэл барааны мэдээлэл буруу байна.";
    default:
      return "Render бүтсэнгүй. Дахин оролдоно уу.";
  }
}
